import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import moment from 'moment';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import {
    debounceTime,
    filter,
    map,
    startWith,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import * as fromDataServices from '../../data-services';
import * as fromLookups from '../../lookups';
import { ScheduledDate } from '../../lookups/store/schedule-date.model';
import * as fromPayroll from '../../payroll/item/store';
import * as fromShared from '../../shared';
import { ResponseStatus } from '../../shared';
import { SelectItem } from '../../shared/efaact-primeng.module';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromUser from '../../user-info';
import { PayrateOverrideService } from '../payrate-override.service';
@Component({
    selector: 'ef-payrate-override-container',
    templateUrl: './payrate-override-container.component.html',
    styleUrls: ['./payrate-override-container.component.scss'],
    standalone: false
})
export class PayrateOverrideContainerComponent
    implements OnInit, OnDestroy, AfterViewInit
{
    payrateOverrideService: EntityCollectionService<fromDataServices.PayrateOverride.PayrateOverride>;
    process$: Observable<fromShared.Process>;
    items$: Observable<fromDataServices.PayrateOverride.PayrateOverride[]>;
    payrollItems$: Observable<fromPayroll.PayrollItem[]>;
    directReports$: Observable<fromLookups.IDirectReport[]>;
    directReportsSelectItems$: Observable<SelectItem[]>;
    activeDirectReportsSelectItems$: Observable<SelectItem[]>;
    payrollItemsSelectItems$: Observable<SelectItem[]>;
    filteredPayrollItemsSelectItems$: Observable<SelectItem[]>;
    userInfo$: Observable<fromUser.IUserInfo>;
    StartDate$: Observable<Date>;
    EndDate$: Observable<Date>;
    scheduledDates$: Observable<ScheduledDate[]>;
    startDatesSelectItems$: Observable<SelectItem[]>;
    endDatesSelectItems$: Observable<SelectItem[]>;
    payperiodSelectItems$: Observable<SelectItem[]>;
    UsersSubject = new ReplaySubject<SelectItem[]>(1);
    PayrollItemSubject = new ReplaySubject<SelectItem[]>(1);
    PayrollItemTypesSubject = new ReplaySubject<SelectItem[]>(1);
    ClearStatusSubject = new ReplaySubject(1);
    FilterSubject = new ReplaySubject<{ StartDate: Date; EndDate: Date }>(1);
    DeleteSubject = new ReplaySubject<
        fromDataServices.PayrateOverride.PayrateOverride[]
    >(1);
    ShowError = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    SelectDirectReportSubject = new ReplaySubject<string>(1);
    private ManageUsersModelDS: EntityCollectionService<fromDataServices.ManageUsers.LoginModel>;
    active = true;
    constructor(
        public entityServices: EntityServices,
        private store: Store,
        private log: LoggingService,
        private service: PayrateOverrideService
    ) {
        this.payrateOverrideService =
            entityServices.getEntityCollectionService<fromDataServices.PayrateOverride.PayrateOverride>(
                fromDataServices.PayrateOverride.entityName
            );
        this.ManageUsersModelDS =
            entityServices.getEntityCollectionService<fromDataServices.ManageUsers.LoginModel>(
                fromDataServices.ManageUsers.entityName
            );
    }
    ngOnInit(): void {}
    ngOnDestroy(): void {
        this.active = false;
        this.payrateOverrideService.clearCache();
    }
    ngAfterViewInit(): void {
        this.ManageUsersModelDS.getWithQuery({
            ShowInactive: 'false',
            UserFilterID: '7'
        });
        setTimeout(() => {
            this.setupObservables();
            this.setupSubscriptions();
        }, 0);
    }
    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.userInfo$ = this.store.pipe(
            select(fromUser.getUserInfo),
            filter(u => !!u)
        );
        this.StartDate$ = this.userInfo$.pipe(map(u => u.PrevPayPeriodStart));
        this.EndDate$ = this.userInfo$.pipe(map(u => u.PrevPayPeriodEnd));

        this.items$ = this.payrateOverrideService.entities$.pipe(
            map(items => items.filter(x => Number(x.id) >= 0)),
            map(items => clone(items))
            // map(items => [this.addTemplate, ...items])
        );

        this.payrollItems$ = this.store.pipe(
            select(fromLookups.getPayrollItemsLookup),
            // map(x => shared.uniqBy(x, y => y.PIListID)),
            map(x => x.filter(y => y.IsActive === true))
        );
        this.filteredPayrollItemsSelectItems$ = combineLatest([
            this.payrollItems$,
            this.SelectDirectReportSubject
        ]).pipe(
            tap(([pi, dr]) =>
                this.log.verbose({
                    type: 'PayrateOverrideContainerComponent:filteredPayrollItemsSelectItems$',
                    pi,
                    dr
                })
            ),
            map(([pi, dr]) => pi.filter(x => x.EmployeeListID === dr)),
            map(x => fromShared.uniqBy(x, y => y.PIListID)),
            map(x => x.filter(y => y.IsActive === true)),
            map(x =>
                x
                    .map(y => ({
                        label: y.PayrollItem,
                        value: y.PIListID
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            )
        );
        this.payrollItemsSelectItems$ = this.payrollItems$.pipe(
            map(x =>
                x
                    .map(y => ({
                        label: y.PayrollItem,
                        value: y.PIListID
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            )
        );

        this.directReports$ = combineLatest([
            this.store.pipe(select(fromLookups.getDirectReportsLookup)),
            this.ManageUsersModelDS.entities$
        ]).pipe(
            filter(([drs, users]) => !!drs && !!users),
            map(([drs, users]) =>
                drs.map(dr => ({
                    ...dr,
                    ...users.find(u => u.EmployeeListID === dr.EmployeeListID)
                }))
            ),
            map(drs => drs.filter(dr => !dr.IsConsultant)),
            map(x => fromShared.uniqBy(x, y => y.EmployeeListID))
        );

        this.directReportsSelectItems$ = this.directReports$.pipe(
            map(x =>
                x
                    .map(y => ({
                        label: y.EmployeeName,
                        value: y.EmployeeListID
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            )
        );
        this.activeDirectReportsSelectItems$ = this.directReports$.pipe(
            map(x =>
                x
                    .filter(y => y.IsActive)
                    .map(y => ({
                        label: y.EmployeeName,
                        value: y.EmployeeListID
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            )
        );

        this.scheduledDates$ = this.store.pipe(
            select(fromLookups.ScheduledDateSelectors.selectAll),
            filter(dates => !!dates && dates.length > 0)
        );
        this.startDatesSelectItems$ = this.scheduledDates$.pipe(
            map(dates =>
                dates
                    .filter(d => d.ScheduledDateType === 'PPE')
                    .map(d => ({
                        label: moment(d.ScheduledStartDate).format(
                            'MM/DD/YYYY'
                        ),
                        value: d.ScheduledStartDate
                    }))
            )
        );
        this.endDatesSelectItems$ = this.scheduledDates$.pipe(
            map(dates =>
                dates
                    .filter(d => d.ScheduledDateType === 'PPE')
                    .map(d => ({
                        label: moment(d.ScheduledEndDate).format('MM/DD/YYYY'),
                        value: d.ScheduledEndDate
                    }))
            )
        );
        this.payperiodSelectItems$ = this.scheduledDates$.pipe(
            map(dates =>
                dates
                    .filter(d => d.ScheduledDateType === 'PPE')
                    .map(d => ({
                        label: `${moment(d.ScheduledStartDate).format(
                            'MM/DD/YYYY'
                        )} - ${moment(d.ScheduledEndDate).format(
                            'MM/DD/YYYY'
                        )}`,
                        value: `${moment(d.ScheduledStartDate).format(
                            'MM/DD/YYYY'
                        )}-${moment(d.ScheduledEndDate).format('MM/DD/YYYY')}`
                    }))
            )
        );
    }
    setupSubscriptions() {
        this.userInfo$
            .pipe(takeWhile(() => this.active))
            .subscribe(userInfo => {
                this.store.dispatch(
                    new fromLookups.LoadDirectReports({
                        user: userInfo,
                        activeOnly: false
                    })
                );
            });
        combineLatest([this.process$, this.ShowError])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
        this.payrateOverrideService.entities$
            .pipe(
                takeWhile(() => this.active),
                debounceTime(100)
            )
            .subscribe(entities => {
                this.log.verbose({
                    type: 'PayrateOverrideContainerComponent:entities$',
                    entities
                });
                this.UsersSubject.next(
                    (
                        fromShared.uniqBy(
                            entities,
                            y => y.EmployeeName
                        ) as fromDataServices.PayrateOverride.PayrateOverride[]
                    )
                        .map(p => ({
                            label: p.EmployeeName,
                            value: p.EmployeeName
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                );
                this.PayrollItemSubject.next(
                    (
                        fromShared.uniqBy(
                            entities,
                            y => y.PayrollItem
                        ) as fromDataServices.PayrateOverride.PayrateOverride[]
                    )
                        .map(p => ({
                            label: p.PayrollItem,
                            value: p.PayrollItem
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                );
                this.PayrollItemTypesSubject.next(
                    (
                        fromShared.uniqBy(
                            entities,
                            y => y.PayrollItemType
                        ) as fromDataServices.PayrateOverride.PayrateOverride[]
                    )
                        .map(p => ({
                            label: p.PayrollItemType,
                            value: p.PayrollItemType
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                );
            });
        this.DeleteSubject.pipe(takeWhile(() => this.active)).subscribe(
            items => {
                this.log.verbose({
                    type: 'PayrateOverrideContainerComponent:onDelete',
                    $event: items
                });
                of(items.map(x => x.id))
                    .pipe(
                        takeWhile(() => this.active),
                        tap(ids =>
                            this.log.verbose({
                                type: 'PayrateOverrideContainerComponent:onDelete:deleting',
                                ids
                            })
                        ),
                        map(ids =>
                            ids.map(id =>
                                this.payrateOverrideService.delete(id)
                            )
                        )
                    )
                    .subscribe(
                        resp => {
                            this.log.verbose({
                                type: 'PayrateOverrideContainerComponent:onDelete:subscribe',
                                resp
                            });
                        },
                        err =>
                            this.log.verbose({
                                type: 'PayrateOverrideContainerComponent:onDelete:subscribe',
                                err
                            })
                    );
            }
        );

        combineLatest([
            this.DeleteSubject.pipe(startWith(null)),
            this.FilterSubject
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([items, dateFilter]) => {
                this.payrateOverrideService.clearCache();
                this.payrateOverrideService.getWithQuery({
                    StartDate: moment(dateFilter.StartDate).format(
                        'MM/DD/YYYY'
                    ),
                    EndDate: moment(dateFilter.EndDate).format('MM/DD/YYYY')
                });
            });
        this.ClearStatusSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.payrateOverrideService.entities$)
        ).subscribe(([, items]) => {
            this.payrateOverrideService.updateManyInCache(
                items.map(item => ({
                    ...item,
                    Status: {
                        StatusCode: ResponseStatus.None,
                        Message: null
                    }
                }))
            );
        });
    }
    onFilter($event) {
        this.log.verbose({
            type: 'PayrateOverrideContainerComponent:onFilter',
            $event
        });
        this.FilterSubject.next($event);
    }
    onSave($event) {
        this.log.verbose({
            type: 'PayrateOverrideContainerComponent:onSave',
            $event
        });
        const item = {
            Ovrid: $event.Ovrid,
            id: $event.Ovrid,
            EmployeeListID: $event.EmployeeListID,
            UserDirectReports: $event.UserDirectReports,
            PIListID: $event.PIListID,
            StartDate: $event.StartDate,
            EndDate: $event.EndDate,
            FromStartDate: $event.FromStartDate,
            FromEndDate: $event.FromEndDate,
            PayRate: $event.PayRate || 0,
            SyncPayrollItems: $event.Ovrid > 0 ? false : true,
            original: { id: $event.Ovrid },
            Row_Version: $event.Row_Version,
            Status: {
                StatusCode: ResponseStatus.Loading,
                Message: 'Saving'
            }
        };
        this.payrateOverrideService.upsert(item);
    }
    onDelete($event: fromDataServices.PayrateOverride.PayrateOverride[]) {
        this.DeleteSubject.next($event);
    }
    onShowError(event) {
        this.ShowError.next(event);
    }
    onClearStatus() {
        this.ClearStatusSubject.next();
    }
    onRecalcRates($event: { StartDate: string; EndDate: string }) {
        this.log.verbose({
            type: 'PayrateOverrideContainerComponent:onRecalcRates',
            $event
        });
        this.service
            .recalcRates($event.StartDate, $event.EndDate)
            .toPromise()
            .then(x => this.onFilter($event));
    }
}
